import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group";
import { Lock } from "lucide-react";
import { cn } from "~/lib/utils";
import {
  ENTITY_TYPE_PLURAL,
  entityIcon,
} from "~/pages/manifold-nextgen/components/search-manager/constants";
import { SEARCH_ENTITY_TYPES, SearchEntityType } from "~/services/search";

/** "projects, datasets, and sessions", for whichever types are given. */
export const typeList = (entityTypes: SearchEntityType[]) =>
  new Intl.ListFormat("en", { type: "conjunction" }).format(
    entityTypes.map((entityType) => ENTITY_TYPE_PLURAL[entityType].toLowerCase())
  );

// Radix toggle items need a non-empty value.
const ALL_TYPES = "all";

interface ScopeFilterProps {
  /** The one type searched, or null for every type. */
  value: SearchEntityType | null;
  onChange: (next: SearchEntityType | null) => void;
  /** Types the caller can see nothing of, shown locked. */
  inaccessible: ReadonlySet<SearchEntityType>;
  size?: "sm" | "default";
  className?: string;
}

/** Pills choosing whether a search covers every entity type or just one. */
export const ScopeFilter = ({
  value,
  onChange,
  inaccessible,
  size = "default",
  className,
}: ScopeFilterProps) => (
  <ToggleGroupPrimitive.Root
    type="single"
    value={value ?? ALL_TYPES}
    onValueChange={(next) => {
      // Radix reports "" when the pressed item is clicked again; a scope is always set.
      if (next) {
        onChange(SEARCH_ENTITY_TYPES.find((entityType) => entityType === next) ?? null);
      }
    }}
    aria-label="Search in"
    className={cn("flex flex-wrap items-center gap-1.5", className)}
  >
    <ScopePill value={ALL_TYPES} size={size}>
      All
    </ScopePill>
    {SEARCH_ENTITY_TYPES.map((entityType) => {
      const isInaccessible = inaccessible.has(entityType);
      return (
        <ScopePill
          key={entityType}
          value={entityType}
          size={size}
          disabled={isInaccessible}
          title={
            isInaccessible
              ? `You don't have access to any ${ENTITY_TYPE_PLURAL[entityType].toLowerCase()}`
              : undefined
          }
        >
          {isInaccessible ? (
            <Lock className="size-3.5" aria-hidden="true" />
          ) : (
            entityIcon(entityType)
          )}
          {ENTITY_TYPE_PLURAL[entityType]}
        </ScopePill>
      );
    })}
  </ToggleGroupPrimitive.Root>
);

const ScopePill = ({
  className,
  size,
  ...props
}: ToggleGroupPrimitive.ToggleGroupItemProps & { size: "sm" | "default" }) => (
  <ToggleGroupPrimitive.Item
    className={cn(
      "inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-border text-muted-foreground transition-colors outline-none",
      size === "sm" ? "h-7 px-2.5 text-xs" : "h-8 px-3 text-sm",
      "hover:bg-muted hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50",
      "data-[state=on]:border-foreground data-[state=on]:bg-foreground data-[state=on]:text-background",
      "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent",
      className
    )}
    {...props}
  />
);
